import { portalText } from "../../i18n/portal.js";
/**
 * Area `billing-operation` of the console gateway — over `apps/api/src/routes/billing-operation/` (design §27.9.2) and
 * plan-014 D3b's payment status routes (`apps/api/src/routes/billing/payment.ts`). Owned by plan-014 unit **D3a (then
 * D3b, D4, E2)**.
 *
 * plan-014 D3b adds what the console's payment status and recovery view reads (design §27.8.16; FR12-BIL-08):
 *
 *   readOperationPayment(id)   `GET /v1/billing-operations/{id}/payment` — what the operation waits on, how to resume
 *   payOperation(id)           `POST /v1/billing-operations/{id}/pay` — "Pay now": the processor's page to open
 *   cancelOperation(id)        `POST /v1/billing-operations/{id}/cancel` (D3a) — only before a payment was asked for
 *
 * A page the person returns to from the processor only READS (FR12-BIL-08): nothing here reports an outcome.
 *
 * plan-014 D4 adds the quote → confirm → status loop Finance → Funding drives (design §27.8.7, §27.8.8; FR12-BIL-06/07):
 *
 *   quoteBillingOperation(accountId, request)              `POST /v1/accounts/{id}/billing-quotes` — a funding change or a
 *                                                          transfer, frozen with its full effect before anything moves
 *   confirmBillingOperation(accountId, quoteId, key)       `POST /v1/accounts/{id}/billing-operations` — the person's
 *                                                          Idempotency-Key, kept per quote, so a retry returns the same
 *                                                          operation instead of making a second one
 *   readBillingOperation(operationId)                      `GET /v1/billing-operations/{id}` — reads, never advances
 *
 * plan-020 **WAPP-PLAN** adds what a plan's card charge needs (design §32.6.1, §32.6.4, §32.6.6; API-CARD's routes):
 *
 *   openSubscriptionCardForm(request)                      `POST /v1/billing/payment-methods/subscription-form` — the
 *                                                          Payform options for a plan quote (or a card replacement):
 *                                                          the server's amount, `frequency_type: recurring`, and that
 *                                                          the form needs a mobile number before Pay enables
 *   confirmSubscriptionOperation(accountId, request)       the confirm above, with the person's `consent` to the
 *                                                          recurring terms and the Payform's `ctn_` token (`card_token`)
 *   replaceSubscriptionCard(billingSubscriptionId, req)    `POST /v1/billing-subscriptions/{id}/card-replacement` — for
 *                                                          WAPP-BILL's card replacement dialog (no screen here)
 *
 * The generated client is reached structurally (D5a's precedent, `./billing-profile.ts`): the operations are in D3a's
 * and D3b's fragments, and the api-client lock holder regenerates the client; until then a call names what is missing.
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import { CURRENCY_CODE, money, type Money } from "@wetalk/domain";

import { openPayform } from "./billing-card.js";
import { assertSubscriptionOk } from "./subscription.js";

import type { ConsoleGateway, HostedCardFormView } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

/** What the person is waiting on (the API's `status`). */
export type PaymentStatusWordView =
  | "awaiting_operator_approval"
  | "pay_now"
  | "draft"
  | "payment_starting"
  | "payment_page_open"
  | "awaiting_payment"
  | "waiting_on_bank"
  | "paid_not_applied"
  | "applying"
  | "completed"
  | "failed"
  | "cancelled";

export const PAYMENT_STATUS_WORD: readonly PaymentStatusWordView[] = [
  "awaiting_operator_approval",
  "pay_now",
  "draft",
  "payment_starting",
  "payment_page_open",
  "awaiting_payment",
  "waiting_on_bank",
  "paid_not_applied",
  "applying",
  "completed",
  "failed",
  "cancelled",
];

export interface OperationPaymentAttemptView {
  readonly chargeAttemptId: string;
  readonly kind: "payment_page" | "card" | "automatic";
  readonly state: string;
  readonly amountMinor: number;
  readonly declineCode: string | null;
  readonly createdAt: string;
  readonly resolvedAt: string | null;
}

export interface OperationPaymentView {
  readonly operationId: string;
  readonly kind: string;
  readonly state: string;
  readonly status: PaymentStatusWordView;
  readonly billingAccountId: string | null;
  readonly amountDueMinor: number;
  readonly netMinor: number | null;
  readonly taxMinor: number | null;
  readonly currency: string;
  readonly failureCode: string | null;
  readonly payUrl: string | null;
  readonly payUrlExpiresAt: string | null;
  readonly payableNow: boolean;
  readonly cancellable: boolean;
  /** Present after "Pay now": the processor's page to open. */
  readonly nextActionUrl: string | null;
  readonly attempt: readonly OperationPaymentAttemptView[];
  readonly createdAt: string;
  readonly completedAt: string | null;
}

/** plan-014 D4: the two requests Finance → Funding quotes (design §27.9.3's union, the D4 members). */
export type FundingQuoteRequestView =
  | {
      readonly kind: "funding_change";
      readonly workspaceId: readonly string[];
      readonly target: { readonly billingAccountId: string } | { readonly newDedicated: true };
    }
  | {
      readonly kind: "transfer";
      readonly fromBillingAccountId: string;
      readonly toBillingAccountId: string;
      readonly amountMinor: number;
      readonly reason: string;
    };

/** One workspace a funding change moves, and what it is doing now (it keeps the balance it started on). */
export interface FundingChangeWorkspaceView {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly fromBillingAccountId: string;
  readonly fromDisplayName: string;
  readonly unbilledTodaySecond: number;
  readonly reservedSecond: number;
  readonly heldConversationCount: number;
}

export interface FundingChangeQuoteView {
  readonly target: { readonly kind: "existing" | "new_dedicated"; readonly billingAccountId: string | null; readonly displayName: string; readonly balanceMinor: number };
  readonly workspace: readonly FundingChangeWorkspaceView[];
  /** Balances left paying for nothing: they keep their credit until it is transferred. */
  readonly retiring: readonly { readonly billingAccountId: string; readonly displayName: string; readonly balanceMinor: number }[];
  readonly conversationInProgress: number;
}

export interface TransferQuoteView {
  readonly from: { readonly billingAccountId: string; readonly displayName: string; readonly heldMinor: number; readonly transferableMinor: number };
  readonly to: { readonly billingAccountId: string; readonly displayName: string };
  readonly amountMinor: number;
  readonly reason: string;
}

/** A frozen quote, decoded for the two D4 kinds (the other kinds' detail is not read here). */
export interface FundingQuoteView {
  readonly billingQuoteId: string;
  readonly kind: "funding_change" | "transfer";
  readonly amountDueMinor: number;
  readonly currency: string;
  readonly expiresAt: string;
  readonly funding: FundingChangeQuoteView | null;
  readonly transfer: TransferQuoteView | null;
}

/** An operation's status (`GET /v1/billing-operations/{id}`). */
export interface BillingOperationStatusView {
  readonly operationId: string;
  readonly kind: string;
  readonly state: string;
  readonly failureCode: string | null;
  readonly result: Readonly<Record<string, unknown>> | null;
}

/** plan-020: what the Payform opens with for a plan charge (design §32.6.1). Every figure is the server's. */
export interface SubscriptionCardFormView {
  readonly purpose: "purchase" | "upgrade" | "reactivation" | "card_replacement";
  readonly establishment: "plan_charge" | "renewal_payment" | "credit_pack";
  readonly billingQuoteId: string | null;
  readonly billingSubscriptionId: string | null;
  readonly billingOperationId: string | null;
  /** Net + VAT: what the form opens with and the card is charged. */
  readonly amount: Money;
  readonly publicKey: string;
  readonly scriptUrl: string;
  readonly frequencyType: "recurring";
  /** The Payform enables Pay only once a mobile number is entered (design §32.12 Q-17). */
  readonly phoneRequired: boolean;
  readonly quoteExpiresAt: string | null;
}

/** plan-020: the person's agreement to the recurring terms (OD-30), under the words of `textRevision`. */
export interface SubscriptionConsentView {
  readonly textRevision: string;
  readonly agreed: true;
}

/** plan-020: a plan quote's confirmation (design §32.6.1). */
export interface SubscriptionConfirmationView {
  readonly billingQuoteId: string;
  readonly idempotencyKey: string;
  /** The purchase terms revision the organization accepted (PD-8), for a kind that buys something. */
  readonly termsRevision: string | null;
  /** Required for a purchase, an upgrade or a reactivation; `null` for a downgrade or a cancellation. */
  readonly consent: SubscriptionConsentView | null;
  /** The Payform's single-use `ctn_` token; `null` when nothing is due. */
  readonly cardToken: string | null;
  /** What the person was shown: the server refuses `409 quote_mismatch` when it is not the quote. */
  readonly kind: string;
  readonly amountDueMinor: number;
}

/** plan-020: what a plan confirmation answered. */
export interface SubscriptionOperationView extends BillingOperationStatusView {
  /** A 3-D Secure (or hosted) page to send the person to, when the charge asks for one. */
  readonly nextActionUrl: string | null;
}

/** plan-020: a card replacement (design §32.6.6; WAPP-BILL's dialog sends it). */
export interface SubscriptionCardReplacementView {
  readonly cardToken: string;
  readonly consent: SubscriptionConsentView;
  readonly idempotencyKey: string;
  /** The credit-pack quote the form opened for, when no renewal is due. */
  readonly billingQuoteId: string | null;
  readonly amountDueMinor: number | null;
}

export interface SubscriptionCardReplacementOutcomeView {
  readonly billingSubscriptionId: string;
  readonly establishment: "renewal_payment" | "credit_pack";
  readonly billingOperationId: string;
  readonly operationState: string;
  readonly amount: Money;
  readonly replaced: boolean;
  readonly card: { readonly paymentMethodId: string; readonly lastFour: string | null; readonly mandateState: string | null } | null;
  readonly nextActionUrl: string | null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-020 WAPP-PLAN: the Payform options for a plan quote, or for replacing a plan's card. */
    openSubscriptionCardForm(request: { readonly billingQuoteId: string } | { readonly purpose: "card_replacement"; readonly billingSubscriptionId: string }): Promise<SubscriptionCardFormView>;
    /** plan-020 WAPP-PLAN: confirms a plan quote with the consent and the card token; one operation per key. */
    confirmSubscriptionOperation(accountId: string, request: SubscriptionConfirmationView): Promise<SubscriptionOperationView>;
    /** plan-020 WAPP-PLAN (for WAPP-BILL): charges a new card for a running plan and repoints the plan to it. */
    replaceSubscriptionCard(billingSubscriptionId: string, request: SubscriptionCardReplacementView): Promise<SubscriptionCardReplacementOutcomeView>;
    /** plan-014 D4: a frozen quote for a funding change or a transfer — the full effect, nothing moved. */
    quoteBillingOperation(accountId: string, request: FundingQuoteRequestView): Promise<FundingQuoteView>;
    /**
     * plan-014 D4: confirms a quote; the same key returns the same operation, never a second one. `termsRevision` — the
     * purchase terms revision the person accepted — is sent for a kind that BUYS something (PD-8; plan-014 GAP).
     */
    confirmBillingOperation(accountId: string, billingQuoteId: string, idempotencyKey: string, termsRevision?: string): Promise<BillingOperationStatusView>;
    /** plan-014 D4: an operation's status — reads, never advances. */
    readBillingOperation(operationId: string): Promise<BillingOperationStatusView>;
    /** What a billing operation is waiting on, and how to resume it. Reads; never advances. */
    readOperationPayment(operationId: string): Promise<OperationPaymentView>;
    /** "Pay now": confirms an automatic top-up waiting for a click, or hands back the page an operation waits on. */
    payOperation(operationId: string): Promise<OperationPaymentView>;
    /** Cancels an operation before any payment was asked for. */
    cancelOperation(operationId: string): Promise<void>;
  }
}

export type BillingOperationGatewaySlice = Pick<
  ConsoleGateway,
  | "readOperationPayment"
  | "payOperation"
  | "cancelOperation"
  | "quoteBillingOperation"
  | "confirmBillingOperation"
  | "readBillingOperation"
>;

/**
 * plan-020 WAPP-PLAN: the plan card charge's methods — their own Pick, so the plan-014 sandbox half of this area
 * (`../sandbox/billing-operation.ts`) keeps its type; their sandbox half is `../sandbox/subscription.ts`.
 */
export type SubscriptionChargeGatewaySlice = Pick<ConsoleGateway, "openSubscriptionCardForm" | "confirmSubscriptionOperation" | "replaceSubscriptionCard">;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in D3a's and D3b's fragments. */
export const billingOperationGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

interface BillingOperationOperations {
  getOperationPayment(request: { path: { billing_operation_id: string } }): Promise<ApiResult>;
  payOperation(request: { path: { billing_operation_id: string } }): Promise<ApiResult>;
  cancelBillingOperation(request: { path: { billing_operation_id: string } }): Promise<ApiResult>;
}

function operationsOf(client: WeTalkApiClient): BillingOperationOperations {
  const held = client as unknown as Partial<Record<keyof BillingOperationOperations, unknown>>;
  return new Proxy({} as BillingOperationOperations, {
    get(_target, name: string) {
      const found = held[name as keyof BillingOperationOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_d2a8e0bd", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function numberOrNull(value: unknown, path: string): number | null {
  return value === null || value === undefined ? null : readWholeNumber(value, path);
}

const ATTEMPT_KIND = ["payment_page", "card", "automatic"] as const;

export function operationPaymentOfWire(data: Readonly<Record<string, unknown>>, path: string): OperationPaymentView {
  const status = PAYMENT_STATUS_WORD.find((one) => one === data["status"]);
  if (status === undefined) throw new Error(`${path}.status: ${JSON.stringify(data["status"])} is not a payment status this console knows`);
  const next = data["next_action"];
  return {
    operationId: readText(data["operation_id"], `${path}.operation_id`),
    kind: readText(data["kind"], `${path}.kind`),
    state: readText(data["state"], `${path}.state`),
    status,
    billingAccountId: textOrNull(data["billing_account_id"], `${path}.billing_account_id`),
    amountDueMinor: readWholeNumber(data["amount_due_minor"], `${path}.amount_due_minor`),
    netMinor: numberOrNull(data["net_minor"], `${path}.net_minor`),
    taxMinor: numberOrNull(data["tax_minor"], `${path}.tax_minor`),
    currency: readText(data["currency"], `${path}.currency`),
    failureCode: textOrNull(data["failure_code"], `${path}.failure_code`),
    payUrl: textOrNull(data["pay_url"], `${path}.pay_url`),
    payUrlExpiresAt: textOrNull(data["pay_url_expires_at"], `${path}.pay_url_expires_at`),
    payableNow: readBoolean(data["payable_now"], `${path}.payable_now`),
    cancellable: readBoolean(data["cancellable"], `${path}.cancellable`),
    nextActionUrl: next === undefined || next === null ? null : readText(readRecord(next, `${path}.next_action`)["url"], `${path}.next_action.url`),
    attempt: readArray(data["attempt"], `${path}.attempt`).map((raw, index) => {
      const one = readRecord(raw, `${path}.attempt[${index}]`);
      const kind = ATTEMPT_KIND.find((allowed) => allowed === one["kind"]);
      if (kind === undefined) throw new Error(`${path}.attempt[${String(index)}].kind is not an attempt kind`);
      return {
        chargeAttemptId: readText(one["charge_attempt_id"], `${path}.attempt.charge_attempt_id`),
        kind,
        state: readText(one["state"], `${path}.attempt.state`),
        amountMinor: readWholeNumber(one["amount_minor"], `${path}.attempt.amount_minor`),
        declineCode: textOrNull(one["decline_code"], `${path}.attempt.decline_code`),
        createdAt: readText(one["created_at"], `${path}.attempt.created_at`),
        resolvedAt: textOrNull(one["resolved_at"], `${path}.attempt.resolved_at`),
      };
    }),
    createdAt: readText(data["created_at"], `${path}.created_at`),
    completedAt: textOrNull(data["completed_at"], `${path}.completed_at`),
  };
}

/** plan-014 D4: the request on the wire (snake_case, design §27.9.3). */
export function fundingQuoteRequestBodyOf(request: FundingQuoteRequestView): Readonly<Record<string, unknown>> {
  if (request.kind === "transfer") {
    return {
      kind: "transfer",
      from_billing_account_id: request.fromBillingAccountId,
      to_billing_account_id: request.toBillingAccountId,
      amount_minor: request.amountMinor,
      reason: request.reason,
    };
  }
  return {
    kind: "funding_change",
    workspace_id: request.workspaceId,
    target: "newDedicated" in request.target ? { new_dedicated: true } : { billing_account_id: request.target.billingAccountId },
  };
}

function numberAt(record: Readonly<Record<string, unknown>>, key: string, path: string): number {
  const value = record[key];
  if (typeof value !== "number" || !Number.isSafeInteger(value)) throw new Error(`${path}.${key} must be a whole number`);
  return value;
}

/** plan-014 D4: a quote of a funding change or a transfer, decoded. */
export function fundingQuoteOfWire(data: Readonly<Record<string, unknown>>, path: string): FundingQuoteView {
  const kind = data["kind"];
  if (kind !== "funding_change" && kind !== "transfer") throw new Error(`${path}.kind ${JSON.stringify(kind)} is not a funding change or a transfer`);
  const detail = readRecord(data["detail"], `${path}.detail`);
  let funding: FundingChangeQuoteView | null = null;
  let transfer: TransferQuoteView | null = null;
  if (kind === "funding_change") {
    const target = readRecord(detail["target"], `${path}.detail.target`);
    funding = {
      target: {
        kind: target["kind"] === "new_dedicated" ? "new_dedicated" : "existing",
        billingAccountId: textOrNull(target["billing_account_id"], `${path}.detail.target.billing_account_id`),
        displayName: readText(target["display_name"], `${path}.detail.target.display_name`),
        balanceMinor: numberAt(target, "balance_minor", `${path}.detail.target`),
      },
      workspace: readArray(detail["workspace"], `${path}.detail.workspace`).map((raw, index) => {
        const one = readRecord(raw, `${path}.detail.workspace[${index}]`);
        const usage = readRecord(one["open_usage"], `${path}.detail.workspace[${index}].open_usage`);
        return {
          workspaceId: readText(one["workspace_id"], `${path}.detail.workspace.workspace_id`),
          workspaceName: readText(one["workspace_name"], `${path}.detail.workspace.workspace_name`),
          fromBillingAccountId: readText(one["from_billing_account_id"], `${path}.detail.workspace.from_billing_account_id`),
          fromDisplayName: readText(one["from_display_name"], `${path}.detail.workspace.from_display_name`),
          unbilledTodaySecond: numberAt(usage, "unbilled_today_second", `${path}.detail.workspace.open_usage`),
          reservedSecond: numberAt(usage, "reserved_second", `${path}.detail.workspace.open_usage`),
          heldConversationCount: numberAt(usage, "held_conversation_count", `${path}.detail.workspace.open_usage`),
        };
      }),
      retiring: readArray(detail["retiring"], `${path}.detail.retiring`).map((raw, index) => {
        const one = readRecord(raw, `${path}.detail.retiring[${index}]`);
        return {
          billingAccountId: readText(one["billing_account_id"], `${path}.detail.retiring.billing_account_id`),
          displayName: readText(one["display_name"], `${path}.detail.retiring.display_name`),
          balanceMinor: numberAt(one, "balance_minor", `${path}.detail.retiring`),
        };
      }),
      conversationInProgress: numberAt(detail, "conversation_in_progress", `${path}.detail`),
    };
  } else {
    const from = readRecord(detail["from"], `${path}.detail.from`);
    const to = readRecord(detail["to"], `${path}.detail.to`);
    transfer = {
      from: {
        billingAccountId: readText(from["billing_account_id"], `${path}.detail.from.billing_account_id`),
        displayName: readText(from["display_name"], `${path}.detail.from.display_name`),
        heldMinor: numberAt(from, "held_minor", `${path}.detail.from`),
        transferableMinor: numberAt(from, "transferable_minor", `${path}.detail.from`),
      },
      to: {
        billingAccountId: readText(to["billing_account_id"], `${path}.detail.to.billing_account_id`),
        displayName: readText(to["display_name"], `${path}.detail.to.display_name`),
      },
      amountMinor: numberAt(detail, "amount_minor", `${path}.detail`),
      reason: readText(detail["reason"], `${path}.detail.reason`),
    };
  }
  return {
    billingQuoteId: readText(data["billing_quote_id"], `${path}.billing_quote_id`),
    kind,
    amountDueMinor: readWholeNumber(data["amount_due_minor"], `${path}.amount_due_minor`),
    currency: readText(data["currency"], `${path}.currency`),
    expiresAt: readText(data["expires_at"], `${path}.expires_at`),
    funding,
    transfer,
  };
}

/** plan-014 D4: an operation's status, decoded (the confirmation's 202 and the status read share these fields). */
export function billingOperationStatusOfWire(data: Readonly<Record<string, unknown>>, path: string): BillingOperationStatusView {
  const result = data["result"];
  return {
    operationId: readText(data["operation_id"], `${path}.operation_id`),
    kind: readText(data["kind"], `${path}.kind`),
    state: readText(data["state"], `${path}.state`),
    failureCode: textOrNull(data["failure_code"], `${path}.failure_code`),
    result: result === null || result === undefined ? null : readRecord(result, `${path}.result`),
  };
}

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

function subscriptionDataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertSubscriptionOk(result), operationId)["data"], `${operationId}.data`);
}

function nextActionUrlOf(value: unknown, path: string): string | null {
  return value === undefined || value === null ? null : readText(readRecord(value, path)["url"], `${path}.url`);
}

const CARD_FORM_PURPOSE = ["purchase", "upgrade", "reactivation", "card_replacement"] as const;
const CARD_FORM_ESTABLISHMENT = ["plan_charge", "renewal_payment", "credit_pack"] as const;

/** plan-020: the Payform options (`SubscriptionCardForm`). Exported for the suites. */
export function subscriptionCardFormOfWire(data: Readonly<Record<string, unknown>>, path: string): SubscriptionCardFormView {
  const purpose = CARD_FORM_PURPOSE.find((one) => one === data["purpose"]);
  if (purpose === undefined) throw new Error(`${path}.purpose is not a card form purpose this console knows`);
  const establishment = CARD_FORM_ESTABLISHMENT.find((one) => one === data["establishment"]);
  if (establishment === undefined) throw new Error(`${path}.establishment is not an establishment this console knows`);
  if (data["frequency_type"] !== "recurring") throw new Error(`${path}.frequency_type: a plan's card is established by a recurring charge only`);
  const currency = readText(data["currency"], `${path}.currency`);
  if (currency !== CURRENCY_CODE) throw new Error(`${path}.currency is not ${CURRENCY_CODE}`);
  return {
    purpose,
    establishment,
    billingQuoteId: textOrNull(data["billing_quote_id"], `${path}.billing_quote_id`),
    billingSubscriptionId: textOrNull(data["billing_subscription_id"], `${path}.billing_subscription_id`),
    billingOperationId: textOrNull(data["billing_operation_id"], `${path}.billing_operation_id`),
    amount: money(readWholeNumber(data["amount_minor"], `${path}.amount_minor`), CURRENCY_CODE),
    publicKey: readText(data["public_key"], `${path}.public_key`),
    scriptUrl: readText(data["script_url"], `${path}.script_url`),
    frequencyType: "recurring",
    phoneRequired: readBoolean(data["phone_required"], `${path}.phone_required`),
    quoteExpiresAt: textOrNull(data["quote_expires_at"], `${path}.quote_expires_at`),
  };
}

/**
 * Mounts EveryPay's Payform for a plan charge and resolves with the `ctn_` token. The mount is the card dialog's own
 * ({@link openPayform}), which reads exactly the processor's key and script, the server's amount and the frequency type
 * from the form view it is handed; this adapter hands it those four from the plan form and nothing typed here.
 */
export function openSubscriptionPayform(form: SubscriptionCardFormView, elementId: string): Promise<string> {
  const opened: HostedCardFormView = {
    creditPackCode: form.purpose,
    amount: form.amount,
    billingQuoteId: form.billingQuoteId ?? form.billingOperationId ?? "",
    net: form.amount,
    tax: money(0, form.amount.currency),
    taxRatePermille: 0,
    publicKey: form.publicKey,
    scriptUrl: form.scriptUrl,
    frequencyType: form.frequencyType,
  };
  return openPayform(opened, elementId);
}

/** plan-020: the confirmation on the wire (`BillingOperationConfirmation`). Exported for the suites. */
export function subscriptionConfirmationBodyOf(request: SubscriptionConfirmationView): Readonly<Record<string, unknown>> {
  return {
    billing_quote_id: request.billingQuoteId,
    kind: request.kind,
    amount_due_minor: request.amountDueMinor,
    ...(request.termsRevision === null ? {} : { terms_revision: request.termsRevision }),
    ...(request.consent === null ? {} : { consent: { text_revision: request.consent.textRevision, agreed: request.consent.agreed } }),
    ...(request.cardToken === null ? {} : { card_token: request.cardToken }),
  };
}

/** plan-020: a card replacement's answer (`CardReplacement`). Exported for the suites. */
export function cardReplacementOfWire(data: Readonly<Record<string, unknown>>, path: string): SubscriptionCardReplacementOutcomeView {
  const establishment = data["establishment"];
  if (establishment !== "renewal_payment" && establishment !== "credit_pack") throw new Error(`${path}.establishment is not an establishment this console knows`);
  const card = data["card"];
  return {
    billingSubscriptionId: readText(data["billing_subscription_id"], `${path}.billing_subscription_id`),
    establishment,
    billingOperationId: readText(data["billing_operation_id"], `${path}.billing_operation_id`),
    operationState: readText(data["operation_state"], `${path}.operation_state`),
    amount: money(readWholeNumber(data["amount_minor"], `${path}.amount_minor`), CURRENCY_CODE),
    replaced: readBoolean(data["replaced"], `${path}.replaced`),
    card:
      card === null || card === undefined
        ? null
        : (() => {
            const held = readRecord(card, `${path}.card`);
            return {
              paymentMethodId: readText(held["payment_method_id"], `${path}.card.payment_method_id`),
              lastFour: textOrNull(held["last_four"], `${path}.card.last_four`),
              mandateState: textOrNull(held["mandate_state"], `${path}.card.mandate_state`),
            };
          })(),
    nextActionUrl: nextActionUrlOf(data["next_action"], `${path}.next_action`),
  };
}

export function billingOperationApiSlice(client: WeTalkApiClient, _context: SliceContext): BillingOperationGatewaySlice & SubscriptionChargeGatewaySlice {
  const operations = operationsOf(client);
  return {
    async quoteBillingOperation(accountId, request) {
      const data = dataOf(await client.createBillingQuote({ path: { account_id: accountId }, body: fundingQuoteRequestBodyOf(request) }), "createBillingQuote");
      return fundingQuoteOfWire(data, "createBillingQuote.data");
    },
    async confirmBillingOperation(accountId, billingQuoteId, idempotencyKey, termsRevision) {
      const data = dataOf(
        await client.confirmBillingOperation({
          path: { account_id: accountId },
          body: { billing_quote_id: billingQuoteId, ...(termsRevision === undefined ? {} : { terms_revision: termsRevision }) },
          header: { "idempotency-key": idempotencyKey },
        }),
        "confirmBillingOperation",
      );
      return billingOperationStatusOfWire(data, "confirmBillingOperation.data");
    },
    async openSubscriptionCardForm(request) {
      const body = "billingQuoteId" in request ? { billing_quote_id: request.billingQuoteId } : { purpose: request.purpose, billing_subscription_id: request.billingSubscriptionId };
      const data = subscriptionDataOf(await client.createSubscriptionCardForm({ body }), "createSubscriptionCardForm");
      return subscriptionCardFormOfWire(data, "createSubscriptionCardForm.data");
    },
    async confirmSubscriptionOperation(accountId, request) {
      const data = subscriptionDataOf(
        await client.confirmBillingOperation({
          path: { account_id: accountId },
          body: subscriptionConfirmationBodyOf(request),
          header: { "idempotency-key": request.idempotencyKey },
        }),
        "confirmBillingOperation",
      );
      return { ...billingOperationStatusOfWire(data, "confirmBillingOperation.data"), nextActionUrl: nextActionUrlOf(data["next_action"], "confirmBillingOperation.data.next_action") };
    },
    async replaceSubscriptionCard(billingSubscriptionId, request) {
      const data = subscriptionDataOf(
        await client.replaceSubscriptionCard({
          path: { billing_subscription_id: billingSubscriptionId },
          body: {
            card_token: request.cardToken,
            consent: { text_revision: request.consent.textRevision, agreed: request.consent.agreed },
            ...(request.billingQuoteId === null ? {} : { billing_quote_id: request.billingQuoteId }),
            ...(request.amountDueMinor === null ? {} : { amount_due_minor: request.amountDueMinor }),
          },
          header: { "idempotency-key": request.idempotencyKey },
        }),
        "replaceSubscriptionCard",
      );
      return cardReplacementOfWire(data, "replaceSubscriptionCard.data");
    },
    async readBillingOperation(operationId) {
      const data = dataOf(await client.getBillingOperation({ path: { billing_operation_id: operationId } }), "getBillingOperation");
      return billingOperationStatusOfWire(data, "getBillingOperation.data");
    },
    async readOperationPayment(operationId) {
      const data = dataOf(await operations.getOperationPayment({ path: { billing_operation_id: operationId } }), "getOperationPayment");
      return operationPaymentOfWire(data, "getOperationPayment.data");
    },
    async payOperation(operationId) {
      const data = dataOf(await operations.payOperation({ path: { billing_operation_id: operationId } }), "payOperation");
      return operationPaymentOfWire(data, "payOperation.data");
    },
    async cancelOperation(operationId) {
      assertOk(await operations.cancelBillingOperation({ path: { billing_operation_id: operationId } }));
    },
  };
}
